import { useTranslation } from "react-i18next";
import { useCandidateEvents } from "@/hooks/useCandidateEvents";
import { useCreateEvent, Event } from "@/hooks/useEvents";
import { CreateEventModal } from "@/components/calendar/CreateEventModal";
import { Button } from "@/components/ui/button";
import { Plus, Calendar, Clock, MapPin, User, Edit } from "lucide-react";
import { useState } from "react";
import { formatCalendarDate } from "@/utils/dateFormatter";

interface CandidateCalendarTabProps {
  candidateId: string;
  candidateName?: string;
}

export const CandidateCalendarTab = ({ candidateId, candidateName }: CandidateCalendarTabProps) => {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  const { data: events = [], isLoading, error, refetch } = useCandidateEvents(candidateId);
  const createEventMutation = useCreateEvent();

  const handleCreateEvent = async (eventData: { 
    title: string; 
    start: Date; 
    end: Date; 
    description?: string; 
    color?: string;
    relations?: {
      candidates: string[];
      offers: string[];
      companies: string[];
      matches: string[];
      contacts: string[];
    };
  }) => {
    // Transform the data to match the Event interface
    const eventWithRelations = {
      title: eventData.title,
      description: eventData.description,
      start_time: eventData.start.toISOString(),
      end_time: eventData.end.toISOString(),
      all_day: false,
      color: eventData.color || '#9b87f5',
      status: 'confirmed' as const,
      priority: 'medium' as const,
      visibility: 'private' as const,
      relations: {
        ...eventData.relations,
        candidates: [candidateId]
      },
      custom_fields: {},
      metadata: {},
      linkedUsers: (eventData as any).linkedUsers
    };
    
    await createEventMutation.mutateAsync(eventWithRelations);
    setIsCreateDialogOpen(false);
    refetch();
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditDialogOpen(false);
    setEditingEvent(null);
    refetch();
  };

  if (error) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-2">
          {t("common.error", "Error")}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {t("calendar.error.loading", "Failed to load calendar events. Please try again.")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t("candidates.tabs.calendar", "Calendar")}</h2>
          <p className="text-gray-500 dark:text-gray-400">{t("candidates.tabs.calendarDescription", "All calendar events linked to this candidate")}</p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("calendar.createEvent", "Create Event")}
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t("calendar.noEvents", "No calendar events")}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t("calendar.noEventsDescription", "No calendar events are linked to this candidate yet.")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: event.color }}
                    />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{event.title}</h3>
                  </div>
                  
                  {event.description && (
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-2">
                      {event.description}
                    </p>
                  )}
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Clock className="h-4 w-4" />
                      <span>
                        {formatCalendarDate(new Date(event.start_time), 'dateTime')}
                        {!event.all_day && (
                          <>
                            {" - "}
                            {formatCalendarDate(new Date(event.end_time), 'time')}
                          </>
                        )}
                      </span>
                    </div>
                    
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <MapPin className="h-4 w-4" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    
                    {event.assignee && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <User className="h-4 w-4" />
                        <span>
                          {event.assignee.first_name} {event.assignee.last_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2 ml-4">
                  <div className="flex items-center gap-2">
                    {event.status && (
                      <span className={`px-2 py-1 text-xs rounded-sm ${
                        event.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                        event.status === 'tentative' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {event.status}
                      </span>
                    )}
                    
                    {event.priority && (
                      <span className={`px-2 py-1 text-xs rounded-sm ${
                        event.priority === 'urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                        event.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                        event.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      }`}>
                        {event.priority}
                      </span>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditEvent(event)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <CreateEventModal
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSave={handleCreateEvent}
        isLoading={createEventMutation.isPending}
        initialData={{
          id: '',
          title: '',
          description: '',
          start: new Date(),
          end: new Date(),
          color: '#9b87f5',
          metadata: {
            candidates: [candidateId],
            offers: [],
            companies: [],
            matches: [],
            contacts: []
          }
        }}
      />

      {/* Edit Event Modal */}
      {editingEvent && (
        <CreateEventModal
          initialData={{
            id: editingEvent.id,
            title: editingEvent.title,
            description: editingEvent.description || "",
            start: new Date(editingEvent.start_time),
            end: new Date(editingEvent.end_time),
            color: editingEvent.color,
            location: editingEvent.location,
            url: editingEvent.url,
            status: editingEvent.status,
            priority: editingEvent.priority,
            all_day: editingEvent.all_day,
            metadata: {
              candidates: editingEvent.relations?.candidates || [],
              offers: editingEvent.relations?.offers || [],
              companies: editingEvent.relations?.companies || [],
              matches: editingEvent.relations?.matches || [],
              contacts: editingEvent.relations?.contacts || [],
              location: editingEvent.location,
              url: editingEvent.url,
              status: editingEvent.status,
              priority: editingEvent.priority,
              all_day: editingEvent.all_day
            }
          }}
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditModal}
          onSave={handleCloseEditModal}
          isEditing={true}
        />
      )}
    </div>
  );
};
